/**
 * The one bar across the top.
 *
 * Everything the app can reach lives here: destinations on the left, search
 * and the window controls on the right. There is no sidebar any more - a
 * permanent rail plus a permanent sidebar plus this was three levels of
 * navigation for eight destinations, and the screens were left with what was
 * over.
 *
 * The strip is also the window's title bar. The whole thing drags except the
 * controls, which opt back out - otherwise the buttons move the window
 * instead of being pressed.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { bridge, isDesktop } from '@/lib/bridge';
import { fileUrl } from '@/lib/api';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import * as Icons from '@/icons';
import { useHistory } from '@/lib/history';
import { UpdatePill } from './UpdatePill';
import { NotificationBell } from './NotificationPeek';
import { useNarrow } from './TabBar';
import { Tip } from './HoverCard';
import { StatusMenu } from './StatusMenu';
import { STAFF_SECTIONS } from '@/views/StaffView';
import { go } from '@/lib/hash';

/** What the strip says it is, on a phone, where there are no tabs to read. */
const TITLES: Record<string, string> = {
  home: 'Home', parties: 'Parties', friends: 'Friends', games: 'Games',
  chat: 'Chat', badges: 'Badges', settings: 'Settings', staff: 'Staff Console', status: 'Server status',
  notifications: 'Notifications', crews: 'Crew'
};

export type NavItem = {
  id: string;
  label: string;
  icon: string;
  badge?: number;
};

/**
 * The destinations, in order.
 *
 * Staff console is appended separately and only for administrators - see
 * below. Everything else is the same for everyone.
 */
const NAV: NavItem[] = [
  { id: 'home', label: 'Home', icon: 'home' },
  { id: 'parties', label: 'Parties', icon: 'squads' },
  { id: 'friends', label: 'Friends', icon: 'friends' },
  { id: 'games', label: 'Games', icon: 'dice' },
  { id: 'chat', label: 'Chat', icon: 'chat' }
];

/**
 * The three that live as icons on the right, beside the bell.
 *
 * Badge, Settings and the staff console are places you go occasionally, not
 * the five things the app is about. Written out in full they took a third of
 * the bar and pushed the search box into the window controls; as icons they
 * sit with the other controls, which is what they are closer to.
 */
const TOOLS: NavItem[] = [
  { id: 'badges', label: 'Badge', icon: 'badges' },
  /* "Crew", not "Crews" - it goes to yours, not to a list of everybody
     else's. Browsing is behind a button on that screen. */
  { id: 'crews', label: 'Crew', icon: 'shield' },
  { id: 'settings', label: 'Settings', icon: 'sliders' }
];

export function TopBar({ route, counts, onNavigate, onSearch, onCreate }: {
  /** Which destination is current, already collapsed to a nav id. */
  route: string;
  counts: { friends: number; invites: number };
  onNavigate: (id: string) => void;
  onSearch: () => void;
  onCreate: () => void;
}) {
  const me = useSession((s) => s.me);
  const connection = useSession((s) => s.connection);
  const [maximized, setMaximized] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const narrow = useNarrow();

  useEffect(() => {
    const api = bridge();
    if (!api?.onWindowState) return;
    return api.onWindowState((s) => setMaximized(!!s.maximized));
  }, []);

  /* Staff console is admin-only, on the interface as well as the route. */
  const admin = me?.role === 'admin';
  const items: NavItem[] = NAV.map((n) =>
    n.id === 'friends' ? { ...n, badge: counts.friends } :
      n.id === 'parties' ? { ...n, badge: counts.invites } : n);

  const tools: NavItem[] = [
    ...TOOLS,
    /* Server health before the console: it is the thing you check in
       passing, rather than sit down to do. */
    ...(admin ? [{ id: 'status', label: 'Server status', icon: 'bolt' }] : []),
    ...(admin ? [{ id: 'staff', label: 'Staff Console', icon: 'shieldNav' }] : [])
  ];


  const headerRef = useRef<HTMLElement>(null);
  const roomy = useRoomy(headerRef);
  const navRef = useRef<HTMLElement>(null);
  const { rail, registerTab } = useRail(navRef, route, roomy);

  return (
    <header ref={headerRef} data-chrome="nav" className="drag relative z-40 flex h-[66px] flex-none items-center gap-1.5 border-b border-line-soft bg-surface-0 pr-2 pl-4">
      <img
        src="./logo.png"
        alt="Pugo"
        width={30}
        height={30}
        className="mr-2 flex-none rounded-[calc(10px*var(--radius-mul,1))] object-contain"
      />

      <BackButton />

      {/* Never scrolls. The labels hide before the bar would ever need a
          scrollbar under it - a strip of chrome you can drag sideways is a
          web page tell. */}
      {narrow ? null : (
      <nav ref={navRef} className="no-drag relative flex min-w-0 shrink items-center gap-0.5 overflow-hidden">
        {items.map((item) => (
          <NavTab
            key={item.id}
            item={item}
            on={route === item.id}
            compact={!roomy}
            onClick={() => onNavigate(item.id)}
            register={registerTab}
          />
        ))}

        {/*
          The lit rail under whichever tab you are on.

          One element for the bar, slid and stretched to the tab you picked,
          rather than one per tab switching on and off - which is what made it
          jump from place to place instead of travelling there.
        */}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-[2px] h-[2.5px] rounded-full bg-accent-hi"
          style={{
            transform: 'translateX(' + rail.x + 'px)',
            width: rail.w,
            opacity: rail.w ? 1 : 0,
            // Just enough spill to look lit rather than printed. The wide
            // second shadow used to throw colour halfway up the tab.
            boxShadow: '0 0 6px color-mix(in srgb, var(--accent-hi) 70%, transparent)',
            transition: rail.settled
              ? 'transform 340ms var(--ease-out-soft), width 340ms var(--ease-out-soft), opacity 160ms linear'
              : 'opacity 160ms linear'
          }}
        />
      </nav>
      )}

      {narrow ? (
        <span className="text-[15px] font-bold">{TITLES[route] ?? 'Pugo'}</span>
      ) : null}

      <div className="min-w-[12px] flex-1" />

      {/* Only present when there is genuinely something to install. */}
      <div className="no-drag mr-1 flex-none"><UpdatePill /></div>

      {/* Search, then the window controls - the order the references use. */}
      <button
        type="button"
        onClick={onSearch}
        title="Search and jump anywhere (Ctrl+K)"
        className="no-drag flex h-[38px] w-[248px] min-w-[42px] shrink items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-inset pr-1.5 pl-2.5 text-[12.5px] text-mute transition-colors hover:border-line hover:text-dim"
      >
        <Icon name="search" size={14} className="flex-none" />
        <span className="min-w-0 flex-1 truncate text-left">Search…</span>
        <kbd className="hidden flex-none rounded border border-line-soft bg-surface-3 px-1.5 py-px font-mono text-[10px] text-ghost sm:block">
          Ctrl K
        </kbd>
      </button>

      {/* Alerts and your own face are controls, not destinations - the nav
          above stays the eight things the app is made of. */}
      {/* The one thing the app is for, always one press away. */}
      <button
        type="button"
        onClick={onCreate}
        title="Create a party"
        className={cn(
          'no-drag ml-2 flex h-[38px] shrink-0 items-center rounded-[calc(10px*var(--radius-mul,1))] bg-accent text-[13px] font-bold text-accent-fg transition-[background-color] hover:bg-accent-hi',
          roomy ? 'gap-1.5 px-4' : 'w-[38px] justify-center'
        )}
      >
        <Icon name="plus" size={roomy ? 14 : 16} />
        {roomy ? <span>Create Party</span> : null}
      </button>

      {/* Badge, Settings and the staff console, as icons.
          Same idea as the tabs on the left - violet when you are on one - but
          without a word each, because these are visited rarely and the bar is
          for the five places you actually move between. */}
      {narrow ? null : (
        <div className="no-drag ml-1.5 hidden shrink-0 items-center gap-0.5 sm:flex">
          {tools.map((tool) => (
            tool.id === 'staff'
              ? <StaffButton key={tool.id} item={tool} on={route === 'staff'} onClick={() => onNavigate('staff')} />
              : <ToolButton
                  key={tool.id}
                  item={tool}
                  on={route === tool.id}
                  onClick={() => onNavigate(tool.id)}
                />
          ))}
        </div>
      )}

      {/* A glance, not a journey - the bell opens its own small panel and
          "See more" is the screen. */}
      <NotificationBell />

      <span className="no-drag relative flex-none">
      <button
        type="button"
        onClick={() => setStatusOpen((v) => !v)}
        title={me ? me.displayName : 'You'}
        aria-label="You and your status"
        className="no-drag relative ml-1 grid h-10 w-10 shrink-0 place-items-center rounded-full outline-none transition-[box-shadow] hover:shadow-[0_0_0_2px_var(--accent-line)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <img
          src={me?.avatarUrl ? fileUrl(me.avatarUrl) : './logo.png'}
          alt=""
          className="h-[33px] w-[33px] rounded-full object-cover"
        />
        <span
          className={cn(
            'absolute right-0 bottom-0 h-[9px] w-[9px] rounded-full ring-2 ring-[var(--bg-0)]',
            connection === 'open' ? 'bg-good' : connection === 'closed' ? 'bg-bad' : 'bg-warn'
          )}
        />
      </button>

      {statusOpen ? <StatusMenu anchorRight onClose={() => setStatusOpen(false)} /> : null}
      </span>

      {isDesktop() ? (
        <div className="no-drag ml-1.5 flex shrink-0 items-center">
          <WinButton label="Minimize" onClick={() => bridge()?.window('minimize')}>
            <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2 6h8" stroke="currentColor" strokeWidth="1.4" /></svg>
          </WinButton>
          <WinButton label={maximized ? 'Restore' : 'Maximize'} onClick={() => bridge()?.window('maximize')}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2.4" y="2.4" width="7.2" height="7.2" rx="1.3" />
            </svg>
          </WinButton>
          <WinButton label="Close" danger onClick={() => bridge()?.window('close')}>
            <svg width="12" height="12" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.4">
              <path d="M3 3l6 6M9 3l-6 6" />
            </svg>
          </WinButton>
        </div>
      ) : null}
    </header>
  );
}

/**
 * One destination.
 *
 * The current one is violet with a bar underneath it, which is the only place
 * in the bar that colour appears - so the eye finds where it is without
 * reading anything.
 */
/**
 * Back to wherever you were.
 *
 * Only there when there is somewhere to go: on the first screen of a session
 * it takes no room at all, rather than sitting greyed out. The trail it walks
 * is kept in lib/history - see there for why the browser's own history is not
 * what is used.
 */
/**
 * Whether the header has room for words.
 *
 * A viewport breakpoint was the wrong tool: what decides this is how much is
 * left after the search box, the create button and the window controls have
 * taken theirs, and that is the header's own width, not the screen's. At
 * 1030px the labels were still switched on and the last tab was being sliced
 * in half by the overflow clip.
 *
 * Two thresholds rather than one, so dragging the window edge across the
 * boundary cannot set the labels flickering on and off.
 */
function useRoomy(ref: React.RefObject<HTMLElement | null>): boolean {
  const [roomy, setRoomy] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (!width) return;
      setRoomy((was) => (was ? width >= 1120 : width >= 1210));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return roomy;
}

/**
 * Where the lit rail should be.
 *
 * Measured from the live buttons rather than computed from widths, because
 * the tabs change size when the labels come off and the count changes with
 * who you are. `settled` stays false for the first placement so the rail
 * appears where it belongs instead of flying in from the left edge on load.
 */
function useRail(
  navRef: React.RefObject<HTMLElement | null>,
  route: string,
  roomy: boolean
) {
  const tabs = useRef(new Map<string, HTMLButtonElement>());
  const [rail, setRail] = useState({ x: 0, w: 0, settled: false });

  const registerTab = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) tabs.current.set(id, el);
    else tabs.current.delete(id);
  }, []);

  const measure = useCallback(() => {
    const el = tabs.current.get(route);
    if (!el) { setRail((r) => ({ ...r, w: 0 })); return; }
    const width = el.offsetWidth;
    setRail((r) => ({
      x: el.offsetLeft + width * 0.18,
      w: width * 0.64,
      // Only animate once something has been drawn in the right place.
      settled: r.w > 0
    }));
  }, [route]);

  useLayoutEffect(measure, [measure, roomy]);

  // The bar can be resized without the route changing.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(nav);
    return () => ro.disconnect();
  }, [navRef, measure]);

  return { rail, registerTab };
}

export function BackButton() {
  const { canBack, back, hint, trail, goBackTo } = useHistory();
  const [open, setOpen] = useState(false);
  const closer = useRef<number | null>(null);
  const wrap = useRef<HTMLSpanElement>(null);

  /*
   * A moment's grace before it closes.
   *
   * The list sits below the button, and a pointer heading for the second row
   * clips the corner of the gap on the way. Closing the instant the cursor
   * left the button meant the thing you were reaching for vanished as you
   * reached for it.
   */
  const show = () => {
    if (closer.current) { clearTimeout(closer.current); closer.current = null; }
    setOpen(true);
  };
  const hide = () => {
    if (closer.current) clearTimeout(closer.current);
    closer.current = window.setTimeout(() => setOpen(false), 220);
  };

  useEffect(() => () => { if (closer.current) clearTimeout(closer.current); }, []);

  /*
   * A backstop for the times mouseleave never arrives.
   *
   * It goes missing more often than it should: a fast exit, a re-render under
   * the cursor, the pointer leaving the window entirely. Watching where the
   * pointer actually is closes the menu in all of those, and costs one
   * listener that only exists while it is open.
   */
  useEffect(() => {
    if (!open) return;
    const check = (e: PointerEvent) => {
      const box = wrap.current;
      if (box && !box.contains(e.target as Node)) hide();
    };
    const leave = () => hide();
    document.addEventListener('pointermove', check);
    document.addEventListener('pointerdown', check);
    window.addEventListener('blur', leave);
    return () => {
      document.removeEventListener('pointermove', check);
      document.removeEventListener('pointerdown', check);
      window.removeEventListener('blur', leave);
    };
  }, [open]);

  if (!canBack) return null;

  return (
    <span ref={wrap} className="no-drag relative mr-1 flex-none" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={back}
        aria-label={hint}
        className={cn(
          'grid h-9 w-9 place-items-center rounded-full transition-colors',
          open ? 'bg-surface-2 text-fg' : 'text-dim hover:bg-surface-2 hover:text-fg'
        )}
      >
        <Icons.ArrowLeft size={17} />
      </button>

      {/*
        Where you have been, newest first.

        Pressing the arrow still just goes back one - this is for skipping
        several at once, which is the case the arrow alone handles badly.
      */}
      {open && trail.length ? (
        <div
          className="absolute top-full left-0 z-50 pt-2"
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          <div className="min-w-[212px] origin-top-left animate-[back-in_150ms_var(--ease-out-soft)] overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border border-line bg-surface-1 py-1.5 shadow-[0_18px_44px_-8px_rgba(0,0,0,.7)]">
            <div className="flex items-center gap-1.5 px-3 pt-1 pb-2 text-[10px] font-extrabold tracking-[.13em] text-ghost uppercase">
              <Icons.ArrowLeft size={11} />
              Recent
            </div>

            {trail.map((entry, i) => (
              <button
                key={entry.path + i}
                type="button"
                onClick={() => { setOpen(false); goBackTo(entry.path); }}
                className="group flex w-full items-center gap-2.5 px-2.5 py-[7px] text-left transition-colors hover:bg-surface-3"
              >
                <span className="grid h-[26px] w-[26px] flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))] bg-surface-3 text-mute transition-colors group-hover:bg-accent-soft group-hover:text-accent-hi">
                  <Icon name={entry.icon} size={14} />
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-dim transition-colors group-hover:text-fg">
                  {entry.label}
                </span>
                {/* How far back it is, so the list reads as a distance. */}
                <span className="flex-none text-[10.5px] font-bold text-ghost tabular-nums">
                  {i + 1}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </span>
  );
}

function NavTab({ item, on, onClick, compact, register }: {
  item: NavItem; on: boolean; onClick: () => void;
  /** No label - just the icon, for a window too narrow to spell it out. */
  compact?: boolean;
  /** Hands the button up so the nav can measure where to put the rail. */
  register: (id: string, el: HTMLButtonElement | null) => void;
}) {
  return (
    <button
      type="button"
      ref={(el) => register(item.id, el)}
      onClick={onClick}
      aria-current={on ? 'page' : undefined}
      title={compact ? item.label : undefined}
      className={cn(
        'relative flex h-[42px] shrink-0 items-center rounded-[calc(10px*var(--radius-mul,1))] text-[14px] font-semibold whitespace-nowrap',
        compact ? 'w-[42px] justify-center' : 'gap-2 px-4',
        'transition-colors duration-150',
        /*
         * Hovering colours the tab; it does not draw a shape behind it.
         *
         * A filled chip appearing under the pointer is a second selection
         * indicator competing with the rail, and on a short wide tab it reads
         * as a pill rather than as a hover. The label and the icon simply take
         * the accent - which is also what the tab you are on looks like, so
         * hovering previews where you are about to be.
         */
        on
          ? 'text-accent-hi'
          : 'text-mute hover:text-accent-hi'
      )}
      /*
       * No bloom behind the tab.
       *
       * A radial wash sat under the current tab and a glow sat on its text,
       * which together read as a smudge rather than a selection - and on the
       * dark bar it looked like the label had a shadow behind it. The colour
       * of the text and the rail underneath are enough to say which tab you
       * are on; anything more is decoration competing with the thing it is
       * meant to point at.
       */
    >
      <Icon name={item.icon} size={compact ? 18 : 17} className="flex-none" />
      {compact ? null : <span>{item.label}</span>}

      {item.badge ? (
        <span className={cn(
          'grid h-[16px] min-w-[16px] place-items-center rounded-full bg-accent px-1 text-[9.5px] font-extrabold text-accent-fg tabular-nums',
          compact && 'absolute top-[2px] right-[1px] ring-2 ring-[var(--bg-0)]'
        )}>
          {item.badge > 9 ? '9+' : item.badge}
        </span>
      ) : null}

    </button>
  );
}

export function WinButton({ children, label, onClick, danger }: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'grid h-8 w-11 place-items-center rounded-md text-mute transition-colors',
        danger ? 'hover:bg-bad hover:text-white' : 'hover:bg-surface-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}


/**
 * One of the three on the right: an icon, and the word only in a tooltip.
 *
 * Lit the same violet as the tabs when you are on it, so "where am I" reads
 * the same whichever side of the bar the answer is on - but without the
 * underline, which belongs to the row of destinations.
 */
/**
 * The staff console, with its sections on hover.
 *
 * Nine screens behind one icon meant always landing on Overview and then
 * hunting for the one you wanted. Hovering offers them directly; pressing
 * still opens the console itself.
 */
export function StaffButton({ item, on, onClick }: { item: NavItem; on: boolean; onClick: () => void }) {
  const [open, setOpen] = useState(false);
  const closer = useRef<number | null>(null);

  const show = () => { if (closer.current) clearTimeout(closer.current); setOpen(true); };
  const hide = () => {
    if (closer.current) clearTimeout(closer.current);
    closer.current = window.setTimeout(() => setOpen(false), 220);
  };
  useEffect(() => () => { if (closer.current) clearTimeout(closer.current); }, []);

  return (
    <span className="relative flex-none" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={onClick}
        aria-label={item.label}
        aria-current={on ? 'page' : undefined}
        className={cn(
          /* Each icon is its own tile - one clear button per job, split apart
             rather than run together in a strip. */
          'grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] border transition-[background-color,border-color,color] duration-[var(--t-hover)]',
          on ? 'border-accent-line bg-accent-soft text-accent-hi' : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-accent-hi'
        )}
      >
        <Icon name={item.icon} size={18} />
      </button>

      {open ? (
        <div className="absolute top-full right-0 z-50 pt-2" onMouseEnter={show} onMouseLeave={hide}>
          <div className="min-w-[186px] origin-top-right animate-[back-in_150ms_var(--ease-out-soft)] overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border border-line bg-surface-1 py-1.5 shadow-[0_18px_44px_-8px_rgba(0,0,0,.7)]">
            <div className="px-3 pt-1 pb-2 text-[10px] font-extrabold tracking-[.13em] text-ghost uppercase">
              Staff console
            </div>
            {STAFF_SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => { setOpen(false); go('staff/' + s.id); }}
                className="group flex w-full items-center gap-2.5 px-2.5 py-[7px] text-left transition-colors hover:bg-surface-3"
              >
                <span className="grid h-[26px] w-[26px] flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))] bg-surface-3 text-mute transition-colors group-hover:bg-accent-soft group-hover:text-accent-hi">
                  <Icon name={s.icon} size={14} />
                </span>
                <span className="flex-1 truncate text-[13px] font-semibold text-dim transition-colors group-hover:text-fg">
                  {s.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </span>
  );
}

export function ToolButton({ item, on, onClick }: { item: NavItem; on: boolean; onClick: () => void }) {
  return (
    <Tip text={item.label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={item.label}
        aria-current={on ? 'page' : undefined}
        className={cn(
          /* Each icon is its own tile - one clear button per job, split apart
             rather than run together in a strip. */
          'grid h-11 w-11 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] border transition-[background-color,border-color,color] duration-[var(--t-hover)]',
          on ? 'border-accent-line bg-accent-soft text-accent-hi' : 'border-line-soft bg-surface-1 text-mute hover:border-line hover:text-accent-hi'
        )}
      >
        <Icon name={item.icon} size={18} />
      </button>
    </Tip>
  );
}
